import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../../firebase/config';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { formatUtcNaiveInstantForViewer } from '../../utils/dateTime';
import useOfferAiSummaries from '../../features/offerAiSummary/useOfferAiSummaries';
import OfferSummaryButton from '../../features/offerAiSummary/OfferSummaryButton';
import OfferAiSummaryModal from '../../features/offerAiSummary/OfferAiSummaryModal';
import useOfferComparison from '../../features/offerComparison/useOfferComparison';
import OfferComparisonStrip from '../../features/offerComparison/OfferComparisonStrip';
import OfferComparisonModal from '../../features/offerComparison/OfferComparisonModal';
import './SellerOffers.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (val) =>
  val ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(val)) : '—';

// Offer.submittedAt/createdAt come back from transaction-service as a bare
// LocalDateTime (UTC digits, no 'Z') — render in the viewer's own
// profile-state timezone, not the device's.
const formatDate = (iso) => {
  if (!iso) return '';
  return formatUtcNaiveInstantForViewer(iso).dateLabel;
};

const priceDiff = (offer, list) => {
  if (!offer || !list) return null;
  const diff = Number(offer) - Number(list);
  const pct  = ((diff / Number(list)) * 100).toFixed(1);
  if (diff > 0) return { label: `+${pct}% over ask`, above: true };
  if (diff < 0) return { label: `${pct}% under ask`, above: false };
  return { label: 'At ask', above: true };
};

const fileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Determine left-border class for card
const cardClass = (offer, offers) => {
  const isTop = offers.length > 0 && offer.id === offers.reduce((best, o) =>
    (Number(o.offerPrice) > Number(best.offerPrice) ? o : best), offers[0]).id;
  if (isTop) return 'top-offer';
  if (offer.status === 'NEW' || !offer.status) return 'new-offer';
  return '';
};

const docIcon = (name = '') => {
  if (name.toLowerCase().includes('rpa') || name.toLowerCase().includes('purchase')) return '📋';
  if (name.toLowerCase().includes('approval') || name.toLowerCase().includes('funds')) return '🏦';
  return '📄';
};

// ── Component ─────────────────────────────────────────────────────────────────

const SellerOffers = ({ transaction, participants, isListingAgent, isSeller, onOfferCount }) => {
  const isProspect = transaction?.status?.toUpperCase() === 'PENDING';
  const listPrice  = transaction?.listPrice || transaction?.propertyPrice || transaction?.price || 0;

  // Offer AI Summary — scoped per transaction. The hook also acts as the
  // access check (list call returns 403 for non-primary parties).
  const offerAi = useOfferAiSummaries(transaction?.id, {
    viewerIsPrimary: isListingAgent || isSeller,
  });

  // Offer Comparison — one per listing, listing-side only. Backend returns
  // 409 until at least two PA offers have READY summaries; hook surfaces that
  // as `tooFew` and the strip adapts its copy.
  const offerComparison = useOfferComparison(transaction?.id, {
    viewerIsPrimary: isListingAgent || isSeller,
  });

  // ── State ──────────────────────────────────────────────────────────────────
  const [offers, setOffers]               = useState([]);
  const [isLoading, setIsLoading]         = useState(true);
  const [error, setError]                 = useState(null);
  const [sortBy, setSortBy]               = useState('newest'); // 'newest' | 'oldest' | 'highest' | 'lowest'

  // Modals
  const [pkgModal, setPkgModal]           = useState(null); // offer object
  const [detailModal, setDetailModal]     = useState(null); // offer object
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Detail modal action state
  const [confirmAction, setConfirmAction] = useState(null); // 'accept' | 'decline'
  const [isActioning, setIsActioning]     = useState(false);

  // Seller interested toggle state
  const [togglingInterestedId, setTogglingInterestedId] = useState(null);
  // Share-with-seller in-flight guard — mirrors togglingInterestedId pattern.
  // Null when no share toggle is pending.
  const [togglingShareWithSellerId, setTogglingShareWithSellerId] = useState(null);

  // Send for signature state
  const [showSendForSigModal, setShowSendForSigModal] = useState(false);
  const [sendForSigOffer, setSendForSigOffer]         = useState(null);
  const [isSendingForSig, setIsSendingForSig]         = useState(false);
  const [sendForSigError, setSendForSigError]         = useState(null);
  const [selectedOfferDocId, setSelectedOfferDocId]   = useState(null);

  // Public offer upload link (listing agent surface)
  const [offerLink, setOfferLink]         = useState(null);
  const [linkCopied, setLinkCopied]       = useState(false);
  const [isTogglingLink, setIsTogglingLink] = useState(false);

  // Source filter — 'all' | 'LINK' | 'MANUAL'
  const [sourceFilter, setSourceFilter]   = useState('all');

  // Upload state
  const [uploadFiles, setUploadFiles]     = useState([]);
  const [isDragging, setIsDragging]       = useState(false);
  const [isUploading, setIsUploading]     = useState(false);
  const [uploadError, setUploadError]     = useState('');
  const [uploadForm, setUploadForm]       = useState({
    offerPrice: '', earnestMoney: '', downPaymentPercent: '',
    closingDays: '30', financingType: 'CONVENTIONAL',
    notes: '',
    // Which rung of the version chain this upload lands on. The backend
    // stamps the actual label (PA_V1 / Counter_V{n}) based on the running
    // count of this type on the transaction.
    documentType: 'PURCHASE_AGREEMENT',
  });

  const fileInputRef = useRef(null);
  const pendingOfferRef = useRef(null); // { offerId, envelopeId } while sender view popup is open

  // ── Firebase token ─────────────────────────────────────────────────────────
  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      const tid = setTimeout(() => { unsub(); reject(new Error('Firebase auth timeout')); }, 10000);
      const unsub = auth.onAuthStateChanged(async (user) => {
        clearTimeout(tid);
        unsub();
        if (user) {
          try { resolve(await user.getIdToken(true)); }
          catch (e) { reject(e); }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }, []);

  // ── Fetch Offers ───────────────────────────────────────────────────────────
  const fetchOffers = useCallback(async () => {
    if (!transaction?.id) { setIsLoading(false); return; }
    setIsLoading(true);
    setError(null);
    try {
      const token = await getFirebaseToken();
      const resp  = await apiClient.get(
        API_ENDPOINTS.transactionOffers(transaction.id),
        { headers: { Authorization: `Bearer ${token}` }, params: { sort: sortBy } }
      );
      const data = resp.data;
      let list = [];
      if (Array.isArray(data))              list = data;
      else if (Array.isArray(data?.offers)) list = data.offers;
      else if (Array.isArray(data?.data))   list = data.data;
      setOffers(list);
      if (onOfferCount) onOfferCount(list.length);
    } catch (err) {
      if (err.response?.status === 404) {
        setOffers([]);
        if (onOfferCount) onOfferCount(0);
      } else {
        console.error('Error fetching offers:', err);
        setError('Failed to load offers. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [transaction?.id, sortBy, getFirebaseToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  // ── Public Offer Upload Link (listing-agent-only) ─────────────────────────
  const fetchOfferLink = useCallback(async () => {
    if (!transaction?.id || !isListingAgent) return;
    try {
      const token = await getFirebaseToken();
      const resp = await apiClient.get(
        API_ENDPOINTS.transactionOfferLink(transaction.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOfferLink(resp.data || null);
    } catch (e) {
      console.error('Failed to load offer upload link:', e);
    }
  }, [transaction?.id, isListingAgent, getFirebaseToken]);

  useEffect(() => { fetchOfferLink(); }, [fetchOfferLink]);

  const handleCopyOfferLink = async () => {
    if (!offerLink?.publicUrl) return;
    try {
      await navigator.clipboard.writeText(offerLink.publicUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (e) {
      console.error('Clipboard copy failed:', e);
    }
  };

  const handleToggleOfferLink = async () => {
    if (!offerLink || isTogglingLink) return;
    setIsTogglingLink(true);
    const nextEnabled = !offerLink.enabled;
    // Optimistic update — the backend is idempotent and the UI feels instant.
    setOfferLink(prev => prev ? { ...prev, enabled: nextEnabled } : prev);
    try {
      const token = await getFirebaseToken();
      const resp = await apiClient.put(
        API_ENDPOINTS.transactionOfferLinkEnabled(transaction.id),
        { enabled: nextEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp?.data) setOfferLink(resp.data);
    } catch (e) {
      console.error('Failed to toggle offer link:', e);
      // Roll back on failure.
      setOfferLink(prev => prev ? { ...prev, enabled: !nextEnabled } : prev);
    } finally {
      setIsTogglingLink(false);
    }
  };

  const offerSource = (offer) => ((offer?.source || 'MANUAL')).toUpperCase();
  const linkOfferCount = offers.filter(o => offerSource(o) === 'LINK').length;
  const manualOfferCount = offers.filter(o => offerSource(o) !== 'LINK').length;

  // ── DocuSign sender view callback — fires when agent sends offer in DocuSign popup ──
  useEffect(() => {
    const handler = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'DOCUSIGN_SENDER_CALLBACK') return;
      if (!event.data?.offerId || event.data?.event !== 'Send') return;
      const pending = pendingOfferRef.current;
      if (!pending?.offerId || !pending?.envelopeId) return;
      pendingOfferRef.current = null;
      try {
        const token = await getFirebaseToken();
        await apiClient.post(
          API_ENDPOINTS.markOfferSent(transaction?.id, pending.offerId),
          { envelopeId: pending.envelopeId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchOffers();
      } catch (e) {
        console.error('Failed to mark offer as sent:', e);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [fetchOffers, getFirebaseToken, transaction?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sorted + filtered offers ───────────────────────────────────────────────
  const filteredOffers = offers.filter(o => {
    if (sourceFilter === 'all') return true;
    const src = (o.source || 'MANUAL').toUpperCase();
    return src === sourceFilter;
  });
  const sortedOffers = [...filteredOffers].sort((a, b) => {
    if (sortBy === 'highest') return Number(b.offerPrice) - Number(a.offerPrice);
    if (sortBy === 'lowest')  return Number(a.offerPrice) - Number(b.offerPrice);
    if (sortBy === 'oldest')  return new Date(a.submittedAt || a.createdAt || 0) - new Date(b.submittedAt || b.createdAt || 0);
    return new Date(b.submittedAt || b.createdAt || 0) - new Date(a.submittedAt || a.createdAt || 0);
  });

  const topOfferId = offers.length > 0
    ? offers.reduce((best, o) => (Number(o.offerPrice) > Number(best.offerPrice) ? o : best), offers[0]).id
    : null;

  // When one offer is marked interested, all others are disabled (mirrors mobile)
  const hasInterestedOffer = offers.some(o => o.sellerInterested);

  // ── Upload handlers ────────────────────────────────────────────────────────
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf');
    if (files.length) setUploadFiles(prev => [...prev, ...files]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type === 'application/pdf');
    if (files.length) setUploadFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const resetUpload = () => {
    setUploadFiles([]);
    setUploadForm({
      offerPrice: '', earnestMoney: '', downPaymentPercent: '',
      closingDays: '30', financingType: 'CONVENTIONAL',
      notes: '',
      documentType: 'PURCHASE_AGREEMENT',
    });
    setUploadError('');
  };

  const handleUploadSubmit = async () => {
    setUploadError('');
    if (!uploadForm.offerPrice.trim()) return setUploadError('Offer price is required.');
    const priceNum = Number(uploadForm.offerPrice.replace(/[^0-9.]/g, ''));
    if (!priceNum || priceNum <= 0) return setUploadError('Offer price must be a positive number.');
    if (uploadFiles.length === 0) return setUploadError('Please attach at least one offer document.');
    if (uploadForm.downPaymentPercent) {
      const dp = Number(uploadForm.downPaymentPercent.replace(/[^0-9.]/g, ''));
      if (isNaN(dp) || dp < 0 || dp > 100) return setUploadError('Down payment must be between 0 and 100.');
    }
    if (uploadForm.closingDays) {
      const coe = Number(uploadForm.closingDays.replace(/[^0-9]/g, ''));
      if (!coe || coe <= 0) return setUploadError('Closing days must be a positive number.');
    }
    const oversized = uploadFiles.find(f => f.size > 10 * 1024 * 1024);
    if (oversized) return setUploadError(`"${oversized.name}" exceeds the 10 MB file size limit.`);

    setIsUploading(true);
    try {
      const token    = await getFirebaseToken();
      const formData = new FormData();
      formData.append('offerPrice',          uploadForm.offerPrice.replace(/[^0-9.]/g, ''));
      formData.append('documentType',        uploadForm.documentType);
      if (uploadForm.earnestMoney.trim())
        formData.append('earnestMoney',      uploadForm.earnestMoney.replace(/[^0-9.]/g, ''));
      if (uploadForm.downPaymentPercent.trim())
        formData.append('downPaymentPercent', uploadForm.downPaymentPercent.replace(/[^0-9.]/g, ''));
      formData.append('financingType',       uploadForm.financingType);
      if (uploadForm.closingDays.trim())
        formData.append('closingDays',       uploadForm.closingDays.replace(/[^0-9]/g, ''));
      if (uploadForm.notes.trim())
        formData.append('notes',             uploadForm.notes.trim());
      if (listPrice)
        formData.append('listPrice',         String(listPrice));
      uploadFiles.forEach((f) => formData.append('files', f));

      await apiClient.post(
        API_ENDPOINTS.transactionOffersUpload(transaction.id),
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      setShowUploadModal(false);
      resetUpload();
      await fetchOffers();
    } catch (err) {
      console.error('Error uploading offer:', err);
      setUploadError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // ── Accept / Decline ───────────────────────────────────────────────────────
  const handleOfferAction = async (action) => {
    if (!detailModal || !isListingAgent) return;
    setIsActioning(true);
    try {
      const token = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.transactionOfferStatus(transaction.id, detailModal.id),
        { status: action === 'accept' ? 'ACCEPTED' : 'DECLINED' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfirmAction(null);
      setDetailModal(null);
      await fetchOffers();
    } catch (err) {
      console.error('Error updating offer status:', err);
      alert(err.response?.data?.message || 'Failed to update offer. Please try again.');
    } finally {
      setIsActioning(false);
    }
  };

  // ── Seller Interested Toggle ───────────────────────────────────────────────
  const handleToggleInterested = async (offer) => {
    if (togglingInterestedId) return;
    setTogglingInterestedId(offer.id);
    try {
      const token = await getFirebaseToken();
      const resp = await apiClient.put(
        API_ENDPOINTS.transactionOfferInterested(transaction.id, offer.id),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update state locally (mirrors mobile: setOffers(prev => prev.map(o => o.id === updated.id ? updated : o)))
      const updated = resp.data?.data || resp.data;
      if (updated?.id) {
        setOffers(prev => prev.map(o => o.id === updated.id ? updated : o));
      } else {
        await fetchOffers();
      }
    } catch (err) {
      console.error('Error toggling interested:', err);
      alert(err.response?.data?.message || 'Failed to update. Please try again.');
    } finally {
      setTogglingInterestedId(null);
    }
  };

  // Per-offer seller-visibility toggle (user ask 2026-10-29). Agent/TC-only —
  // the backend rejects seller callers with 401, and we gate the UI on
  // isListingAgent so the button never surfaces to the seller view.
  // Grandfathered rows (sharedWithRoles == null) render as "Shared" today;
  // unshare materialises an explicit empty set server-side so the seller
  // stops seeing them going forward.
  const handleToggleShareWithSeller = async (offer) => {
    if (togglingShareWithSellerId) return;
    setTogglingShareWithSellerId(offer.id);
    try {
      const token = await getFirebaseToken();
      const roles = Array.isArray(offer.sharedWithRoles) ? offer.sharedWithRoles : null;
      // Null (grandfathered) is treated as "shared" so the next action is
      // Unshare — matches the badge logic in renderBadges below.
      const isCurrentlyShared = roles === null || roles.includes('SELLER');
      const url = isCurrentlyShared
        ? API_ENDPOINTS.unshareOfferWithSeller(transaction.id, offer.id)
        : API_ENDPOINTS.shareOfferWithSeller(transaction.id, offer.id);
      const resp = await apiClient.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      const updated = resp.data?.data || resp.data;
      if (updated?.id) {
        setOffers(prev => prev.map(o => o.id === updated.id ? updated : o));
      } else {
        await fetchOffers();
      }
    } catch (err) {
      console.error('Error toggling share-with-seller:', err);
      alert(err.response?.data?.message || 'Failed to update share status. Please try again.');
    } finally {
      setTogglingShareWithSellerId(null);
    }
  };

  // ── Send for Signature ─────────────────────────────────────────────────────
  const handleSendForSignature = async () => {
    if (!sendForSigOffer) return;
    setIsSendingForSig(true);
    setSendForSigError(null);
    try {
      const token = await getFirebaseToken();
      const returnUrl = `${window.location.origin}/docusign-sender-callback?txId=${transaction.id}&offerId=${sendForSigOffer.id}`;
      const resp = await apiClient.post(
        API_ENDPOINTS.sendOfferForSignature(transaction.id, sendForSigOffer.id),
        { returnUrl, documentId: selectedOfferDocId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update offer in list with returned data (mirrors mobile setOffers local update)
      const updated = resp.data?.data || resp.data;
      if (updated?.id) {
        setOffers(prev => prev.map(o => o.id === updated.id ? updated : o));
      } else {
        await fetchOffers();
      }
      setShowSendForSigModal(false);
      setSendForSigOffer(null);
      setDetailModal(null);
      // Open DocuSign sender view popup — agent reviews and clicks Send
      const senderViewUrl = resp.data?.senderViewUrl || updated?.senderViewUrl;
      const envelopeId = resp.data?.envelopeId || updated?.envelopeId;
      if (senderViewUrl) {
        pendingOfferRef.current = { offerId: updated?.id || sendForSigOffer.id, envelopeId };
        window.open(senderViewUrl, '_blank');
      }
    } catch (err) {
      console.error('Error sending for signature:', err);
      // Extract message from all possible backend error structures (mirrors mobile service pattern)
      const msg = err.response?.data?.message
        || err.response?.data?.error?.message
        || err.message
        || 'Failed to send for signature. Please try again.';
      // Detect DocuSign-specific errors (mirrors mobile isDocuSign detection)
      const isDocuSign = /docusign|invalid_grant|access.?token|user_not_found/i.test(msg);
      setSendForSigError(
        isDocuSign
          ? 'DocuSign is not connected to your account. Please connect DocuSign in your profile settings to send documents for signature.'
          : msg
      );
    } finally {
      setIsSendingForSig(false);
    }
  };

  // ── Document Download (authenticated) ─────────────────────────────────────
  const handleDownloadDoc = async (offer, doc) => {
    if (!doc?.documentId) {
      if (doc?.url) { window.open(doc.url, '_blank'); return; }
      return;
    }
    try {
      const token = await getFirebaseToken();
      const resp = await apiClient.get(
        API_ENDPOINTS.downloadOfferDocument(transaction.id, offer.id, doc.documentId),
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || doc.fileName || 'document.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Failed to download document. Please try again.');
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────
  const renderBadges = (offer, isTop) => {
    const badges = [];
    if (isTop) badges.push(<span key="top" className="so-badge so-badge-top">⭐ Top Offer</span>);
    if (offer.sellerInterested) badges.push(<span key="si" className="so-badge so-badge-interested">❤ Seller Interested</span>);
    // Source tag — MANUAL vs VIA LINK. Null/undefined source is treated as
    // MANUAL for pre-feature offers.
    const src = (offer.source || 'MANUAL').toUpperCase();
    if (src === 'LINK') {
      badges.push(<span key="src" className="so-badge so-badge-via-link">Via Link</span>);
    } else {
      badges.push(<span key="src" className="so-badge so-badge-manual">Manual</span>);
    }
    // UPDATED tag — any document added strictly after the initial submittedAt
    // means the submitter appended to this offer.
    if (hasPostSubmitDoc(offer)) {
      badges.push(<span key="upd" className="so-badge so-badge-updated">Updated</span>);
    }
    // Shared-with-seller badge (user ask 2026-10-29). Null sharedWithRoles =
    // grandfathered row from before this feature — treated as shared so the
    // agent still sees the state. Empty set = unshared, no badge.
    const sharedRoles = Array.isArray(offer.sharedWithRoles) ? offer.sharedWithRoles : null;
    if (sharedRoles === null || sharedRoles.includes('SELLER')) {
      badges.push(<span key="shared-seller" className="so-badge so-badge-shared-with-seller">Shared with Seller</span>);
    }

    const st = (offer.status || '').toUpperCase();
    if (st === 'ACCEPTED')  badges.push(<span key="st" className="so-badge so-badge-accepted">Accepted</span>);
    else if (st === 'REJECTED' || st === 'DECLINED') badges.push(<span key="st" className="so-badge so-badge-declined">Declined</span>);
    else if (st === 'REVIEWED')  badges.push(<span key="st" className="so-badge so-badge-reviewed">Reviewed</span>);
    else if (st === 'COUNTERED') badges.push(<span key="st" className="so-badge so-badge-countered">Countered</span>);
    else if (st === 'WITHDRAWN') badges.push(<span key="st" className="so-badge so-badge-withdrawn">Withdrawn</span>);
    else if (st === 'EXPIRED')   badges.push(<span key="st" className="so-badge so-badge-withdrawn">Expired</span>);
    else badges.push(<span key="new" className="so-badge so-badge-new">New</span>);

    const lt = (offer.loanType || offer.financingType || '').toUpperCase();
    if (lt === 'CASH' || lt === 'ALL CASH' || lt === 'ALL_CASH') badges.push(<span key="lt" className="so-badge so-badge-cash">💰 All Cash</span>);
    else if (lt) badges.push(<span key="lt" className="so-badge so-badge-financed">{offer.loanType || offer.financingType}</span>);

    return badges;
  };

  const renderPackageDocs = (offer) => {
    const docs = offer.documents || [];
    const EXPECTED = ['Purchase Agreement', 'Pre-Approval Letter / Proof of Funds', 'Agency Disclosure', 'Wire Fraud Advisory'];
    if (docs.length === 0) return EXPECTED.map(n => ({ name: n, ready: false, file: null, uploadedAt: null }));
    // Use doc.status === 'RECEIVED' from API (mirrors mobile OffersTab)
    return docs.map(d => ({
      name: d.name || d.fileName || 'Document',
      ready: d.status === 'RECEIVED' || d.status == null,
      file: d,
      uploadedAt: d.uploadedAt || null,
    }));
  };

  // A document counts as "post-submit" (= appended via link later) when its
  // uploadedAt is strictly after the offer's submittedAt. Pre-feature docs
  // without uploadedAt fall through to false — no false positives.
  const hasPostSubmitDoc = (offer) => {
    const submitted = offer?.submittedAt;
    if (!submitted || !Array.isArray(offer?.documents)) return false;
    const submittedTs = new Date(submitted).getTime();
    if (Number.isNaN(submittedTs)) return false;
    return offer.documents.some(d => {
      if (!d?.uploadedAt) return false;
      const t = new Date(d.uploadedAt).getTime();
      return !Number.isNaN(t) && t > submittedTs + 1000; // 1s slack
    });
  };

  const fmtContingencies = (c) => {
    if (!c) return null;
    if (Array.isArray(c)) return c.length > 0 ? c.join(', ') : null;
    return c || null;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="so-container">

      {/* Header */}
      <div className="so-header">
        <div className="so-header-left">
          <h2 className="so-page-title">Offers Received</h2>
          <p className="so-offer-count">
            {isLoading ? 'Loading…' : `${offers.length} offer${offers.length !== 1 ? 's' : ''}${listPrice ? ` · List price ${formatCurrency(listPrice)}` : ''}`}
          </p>
        </div>
        <div className="so-header-actions">
          {offers.length > 1 && (
            <select
              className="so-sort-btn"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="newest">⇅ Newest First</option>
              <option value="oldest">⇅ Oldest First</option>
              <option value="highest">⇅ Highest Price</option>
              <option value="lowest">⇅ Lowest Price</option>
            </select>
          )}
          {/* Upload button — listing agent + prospect phase only */}
          {isListingAgent && (
            <button
              className="so-upload-btn"
              onClick={() => { resetUpload(); setShowUploadModal(true); }}
              disabled={!isProspect}
              title={!isProspect ? 'Offer upload is only available during the Prospect phase' : ''}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Upload Offer Package
            </button>
          )}
        </div>
      </div>

      {/* Offer Comparison narrative strip — ≥2 PA offers, listing-side only */}
      <OfferComparisonStrip
        paOfferCount={offers.filter((o) => !o.documentType || o.documentType === 'PURCHASE_AGREEMENT').length}
        offerComparison={offerComparison}
      />

      {/* Public offer upload link — listing agent only */}
      {isListingAgent && offerLink && (
        <div className={`so-offer-link-panel${!offerLink.enabled ? ' so-offer-link-panel-off' : ''}`}>
          <div className="so-offer-link-header">
            <div className="so-offer-link-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </div>
            <div className="so-offer-link-text">
              <p className="so-offer-link-title">Offer upload link</p>
              <p className="so-offer-link-sub">Post this link on the MLS or share it directly. Anyone with the link can submit an offer after verifying their email.</p>
            </div>
            <button
              type="button"
              className={`so-offer-link-switch${offerLink.enabled ? ' on' : ''}`}
              role="switch"
              aria-checked={offerLink.enabled}
              aria-label={offerLink.enabled ? 'Disable offer upload link' : 'Enable offer upload link'}
              disabled={isTogglingLink}
              onClick={handleToggleOfferLink}
            >
              <span className="so-offer-link-switch-thumb" />
            </button>
          </div>
          <div className="so-offer-link-row">
            <input
              className="so-offer-link-input"
              value={offerLink.publicUrl}
              readOnly
              onFocus={(e) => e.target.select()}
            />
            <button
              className="so-offer-link-copy-btn"
              type="button"
              onClick={handleCopyOfferLink}
              disabled={!offerLink.enabled}
            >
              {linkCopied ? 'Copied' : 'Copy link'}
            </button>
          </div>
          <p className={`so-offer-link-status${offerLink.enabled ? ' on' : ' off'}`}>
            {offerLink.enabled
              ? 'Link is active. Accepting offers.'
              : 'Link is off. Anyone opening it sees a "not accepting offers" message.'}
          </p>
        </div>
      )}

      {/* Source filter chips — only when there is at least one offer */}
      {offers.length > 0 && (
        <div className="so-source-filter">
          <button
            type="button"
            className={`so-source-chip${sourceFilter === 'all' ? ' active' : ''}`}
            onClick={() => setSourceFilter('all')}
          >
            All ({offers.length})
          </button>
          <button
            type="button"
            className={`so-source-chip${sourceFilter === 'LINK' ? ' active' : ''}`}
            onClick={() => setSourceFilter('LINK')}
          >
            Via link ({linkOfferCount})
          </button>
          <button
            type="button"
            className={`so-source-chip${sourceFilter === 'MANUAL' ? ' active' : ''}`}
            onClick={() => setSourceFilter('MANUAL')}
          >
            Manual ({manualOfferCount})
          </button>
        </div>
      )}

      {/* Seller info notice */}
      {isSeller && (
        <div className="so-readonly-banner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          Review all received offers and mark the one you're most interested in. Your agent will manage the final acceptance and signature process.
        </div>
      )}

      {/* Upload banner — listing agent + prospect phase only */}
      {isListingAgent && isProspect && (
        <div className="so-upload-banner" onClick={() => { resetUpload(); setShowUploadModal(true); }}>
          <div className="so-upload-banner-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
          <div className="so-upload-banner-text">
            <p className="so-upload-banner-title">Upload Offer Package</p>
            <p className="so-upload-banner-sub">Upload the full package from the buyer's agent — Purchase Agreement, Pre-Approval, Agency Disclosure, Wire Fraud Advisory and any addenda</p>
          </div>
          <span className="so-upload-banner-arrow">+</span>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="so-loading">
          <div className="so-loading-spinner" />
          <span>Loading offers…</span>
        </div>
      ) : error ? (
        <div className="so-error">
          <div className="so-empty-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <h3>Error Loading Offers</h3>
          <p>{error}</p>
          <button className="so-retry-btn" onClick={fetchOffers}>Try Again</button>
        </div>
      ) : offers.length === 0 ? (
        <div className="so-empty">
          <div className="so-empty-icon">
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#C7C7CC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
          </div>
          <h3>No Offers Yet</h3>
          <p>{isListingAgent ? "Upload offer packages received from buyer's agents to get started." : "No offers have been received yet. You'll be notified when your agent uploads an offer."}</p>
          {isListingAgent && isProspect && (
            <button className="so-retry-btn" onClick={() => { resetUpload(); setShowUploadModal(true); }}>
              Upload First Offer
            </button>
          )}
        </div>
      ) : (
        <div className="so-grid">
          {sortedOffers.map((offer) => {
            const isTop  = offer.isTopOffer || offer.id === topOfferId;
            const diff   = priceDiff(offer.offerPrice, listPrice);
            const docs   = renderPackageDocs(offer);
            const cClass = cardClass(offer, offers);
            // Mirrors mobile: when another offer is already interested, disable this card
            const interestedDisabled = hasInterestedOffer && !offer.sellerInterested;

            return (
              <div key={offer.id} className={`so-card ${cClass}${interestedDisabled ? ' so-card-disabled' : ''}`}>
                <div className="so-card-body">

                  {/* Badges */}
                  <div className="so-badge-row">
                    {renderBadges(offer, isTop)}
                  </div>

                  {/* Buyer + date */}
                  <div className="so-card-top">
                    <div>
                      <div className="so-buyer-name">{offer.buyerName || 'Unknown Buyer'}</div>
                      <div className="so-buyer-agent">
                        {offer.buyerAgentName ? `Agent: ${offer.buyerAgentName}` : ''}
                        {offer.buyerAgentCompany ? ` · ${offer.buyerAgentCompany}` : ''}
                      </div>
                    </div>
                    <div className="so-offer-date">{formatDate(offer.submittedAt || offer.createdAt)}</div>
                  </div>

                  {/* Price */}
                  <div className="so-price-row">
                    <div className="so-offer-price">{formatCurrency(offer.offerPrice)}</div>
                    {diff && (
                      <div className={`so-price-vs-list${diff.above ? '' : ' below'}`}>{diff.label}</div>
                    )}
                  </div>

                  {/* Summary chips */}
                  <div className="so-chips">
                    {offer.earnestMoney       && <div className="so-chip"><strong>EMD</strong> {formatCurrency(offer.earnestMoney)}</div>}
                    {offer.downPaymentPercent != null && <div className="so-chip"><strong>Down</strong> {offer.downPaymentPercent}%</div>}
                    {offer.closingDays        && <div className="so-chip"><strong>COE</strong> {offer.closingDays} days</div>}
                    {fmtContingencies(offer.contingencies) && <div className="so-chip"><strong>Contingencies</strong> {fmtContingencies(offer.contingencies)}</div>}
                  </div>

                  {/* Signing order — shown after Send for Signature (envelopeId set) */}
                  {offer.envelopeId && (() => {
                    const allTx = transaction?.participants || [];
                    const agent = allTx.find(p => (p.role || '').toUpperCase() === 'LISTING_AGENT');
                    const rest  = allTx.filter(p => (p.role || '').toUpperCase() !== 'LISTING_AGENT');
                    const orderedSigners = [agent, ...rest].filter(Boolean);
                    if (!orderedSigners.length) return null;
                    const initials = (name = '') => {
                      const parts = name.trim().split(/\s+/);
                      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
                    };
                    const CARD_ROLE_LABEL = { LISTING_AGENT: 'Listing Agent', SELLER: 'Seller', BUYER_AGENT: "Buyer's Agent", BUYER: 'Buyer', AGENT: 'Agent', TRANSACTION_COORDINATOR: 'Transaction Coordinator' };
                    const ordinal = (n) => n === 1 ? 'first' : n === 2 ? 'second' : n === 3 ? 'third' : `${n}th`;
                    return (
                      <div className="so-signing-section">
                        <div className="so-signing-header">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                          <span className="so-signing-title">RPA Acceptance — Signing Order</span>
                        </div>
                        {orderedSigners.map((signer, idx) => {
                          const roleKey = (signer.role || '').toUpperCase();
                          const label = CARD_ROLE_LABEL[roleKey] || signer.role || 'Participant';
                          return (
                            <div key={signer.id || idx} className="so-signer-row">
                              <div className="so-signer-avatar">{initials(signer.name || signer.displayName || '')}</div>
                              <div className="so-signer-info">
                                <span className="so-signer-name">{signer.name || signer.displayName}</span>
                                <span className="so-signer-role">{label} · signs {ordinal(idx + 1)}</span>
                              </div>
                              <span className="so-signer-check">✓</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Package mini-preview */}
                  <div className="so-pkg-section">
                    <div className="so-pkg-label">Package ({docs.filter(d => d.ready).length}/{docs.length} docs)</div>
                    {docs.map((d, i) => (
                      <div key={i} className="so-pkg-row">
                        <div className={`so-pkg-dot ${d.ready ? 'ready' : 'missing'}`} />
                        <span className="so-pkg-doc-name">{d.name}</span>
                        <span className={`so-pkg-doc-tag ${d.ready ? 'ready' : 'missing'}`}>{d.ready ? '✓' : 'Missing'}</span>
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="so-card-actions">
                    <button
                      className="so-act-btn so-act-ghost"
                      onClick={() => setPkgModal(offer)}
                      disabled={interestedDisabled}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      View Package
                    </button>
                    {/* Send for Signature — listing agent only, only on sellerInterested offer */}
                    {isListingAgent && offer.sellerInterested && (() => {
                      const isSent = offer.envelopeStatus === 'sent' || offer.envelopeStatus === 'completed';
                      return (
                        <button
                          className={`so-act-btn ${isSent ? 'so-act-sig-sent' : 'so-act-sig'}`}
                          onClick={() => { if (!isSent) { setSendForSigOffer(offer); setSendForSigError(null); setSelectedOfferDocId(null); setShowSendForSigModal(true); } }}
                          disabled={isSent}
                          title={isSent ? 'Already sent to DocuSign' : 'Send offer for DocuSign signature'}
                        >
                          {isSent ? '✓ Sent' : '✍ Send for Signature'}
                        </button>
                      );
                    })()}
                    {/* Mark Interested — seller only (mirrors mobile: isSeller only) */}
                    {isSeller && (
                      <button
                        className={`so-act-btn ${offer.sellerInterested ? 'so-act-interested-on' : 'so-act-interested'}`}
                        onClick={() => handleToggleInterested(offer)}
                        disabled={interestedDisabled || togglingInterestedId === offer.id}
                        title={interestedDisabled ? 'Another offer is already marked as interested' : (offer.sellerInterested ? 'Remove interest' : 'Mark as interested')}
                      >
                        {togglingInterestedId === offer.id ? '…' : (offer.sellerInterested ? '✓ Interested' : '☆ Mark Interested')}
                      </button>
                    )}
                    {/* Share with Seller — listing agent / TC only (mirrors mobile).
                        Grandfathered rows (null sharedWithRoles) render as "Unshare"
                        so the agent can hide them going forward. Backend materialises
                        an explicit empty set on unshare so the grandfather signal
                        is retired for that row. */}
                    {isListingAgent && (() => {
                      const sharedRoles = Array.isArray(offer.sharedWithRoles) ? offer.sharedWithRoles : null;
                      const isShared = sharedRoles === null || sharedRoles.includes('SELLER');
                      const pending = togglingShareWithSellerId === offer.id;
                      return (
                        <button
                          className={`so-act-btn ${isShared ? 'so-act-unshare-seller' : 'so-act-share-seller'}`}
                          onClick={() => handleToggleShareWithSeller(offer)}
                          disabled={pending}
                          title={isShared ? 'Stop showing this offer to the seller' : 'Make this offer visible to the seller'}
                        >
                          {pending ? '…' : (isShared ? '🙈 Unshare' : '👁 Share with Seller')}
                        </button>
                      );
                    })()}
                    <button
                      className="so-act-btn so-act-primary"
                      onClick={() => { setConfirmAction(null); setDetailModal(offer); }}
                      disabled={interestedDisabled}
                    >
                      View Details →
                    </button>
                    <OfferSummaryButton offerAi={offerAi} offerId={offer.id} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── View Package Modal ──────────────────────────────────────────────── */}
      {pkgModal && (
        <div className="so-modal-overlay" onClick={() => setPkgModal(null)}>
          <div className="so-modal" onClick={e => e.stopPropagation()}>
            <div className="so-modal-header">
              <div>
                <p className="so-modal-title">{pkgModal.buyerName} — Package</p>
                <p className="so-modal-sub">
                  {renderPackageDocs(pkgModal).filter(d => d.ready).length} documents · Uploaded {formatDate(pkgModal.submittedAt || pkgModal.createdAt)}
                </p>
              </div>
              <button className="so-modal-close" onClick={() => setPkgModal(null)}>✕</button>
            </div>
            <div className="so-modal-body">
              {renderPackageDocs(pkgModal).map((d, i) => {
                const submittedTs = pkgModal.submittedAt ? new Date(pkgModal.submittedAt).getTime() : 0;
                const uploadedTs = d.uploadedAt ? new Date(d.uploadedAt).getTime() : 0;
                const isAppended = uploadedTs > submittedTs + 1000;
                return (
                  <div key={i} className={`so-doc-row${d.ready ? '' : ' missing'}`}>
                    <span className="so-doc-icon">{docIcon(d.name)}</span>
                    <div className="so-doc-info">
                      <div className="so-doc-name-row">
                        <span className="so-doc-name">{d.name}</span>
                        {isAppended && <span className="so-doc-added-tag">Added</span>}
                      </div>
                      {d.uploadedAt && (
                        <div className="so-doc-date">Uploaded {formatDate(d.uploadedAt)}</div>
                      )}
                    </div>
                    {d.ready ? (
                      <>
                        {d.file?.size && <span className="so-doc-size">{fileSize(d.file.size)}</span>}
                        <button className="so-doc-view-btn" onClick={e => { e.stopPropagation(); handleDownloadDoc(pkgModal, d.file); }}>Download</button>
                      </>
                    ) : (
                      <span className="so-doc-missing-label">Not uploaded</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="so-modal-footer">
              <button className="so-footer-btn so-footer-cancel" onClick={() => setPkgModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Details Modal ──────────────────────────────────────────────── */}
      {detailModal && (() => {
        const diff = priceDiff(detailModal.offerPrice, listPrice);
        return (
          <div className="so-modal-overlay" onClick={() => { setDetailModal(null); setConfirmAction(null); }}>
            <div className="so-modal" style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>

              {/* Confirm overlay (accept / decline) */}
              {confirmAction && (
                <div className="so-confirm-overlay">
                  <p className="so-confirm-title">
                    {confirmAction === 'accept' ? '✓ Accept this offer?' : '✕ Decline this offer?'}
                  </p>
                  <p className="so-confirm-sub">
                    {confirmAction === 'accept'
                      ? `Accept offer from ${detailModal.buyerName} for ${formatCurrency(detailModal.offerPrice)}?`
                      : `This will mark the offer from ${detailModal.buyerName} as declined.`}
                  </p>
                  <div className="so-confirm-row">
                    <button className="so-footer-btn so-footer-cancel" style={{ flex: 'unset', padding: '10px 20px' }} onClick={() => setConfirmAction(null)} disabled={isActioning}>
                      Cancel
                    </button>
                    <button
                      className={`so-footer-btn ${confirmAction === 'accept' ? 'so-footer-accept' : 'so-footer-decline'}`}
                      style={{ flex: 'unset', padding: '10px 20px' }}
                      onClick={() => handleOfferAction(confirmAction)}
                      disabled={isActioning}
                    >
                      {isActioning ? 'Processing…' : (confirmAction === 'accept' ? 'Yes, Accept' : 'Yes, Decline')}
                    </button>
                  </div>
                </div>
              )}

              <div className="so-modal-header">
                <div>
                  <p className="so-modal-title">{detailModal.buyerName} — Offer Detail</p>
                  <p className="so-modal-sub">
                    Received {formatDate(detailModal.submittedAt || detailModal.createdAt)}
                    {detailModal.buyerAgentName ? ` · Agent: ${detailModal.buyerAgentName}` : ''}
                    {detailModal.buyerAgentCompany ? ` · ${detailModal.buyerAgentCompany}` : ''}
                  </p>
                </div>
                <button className="so-modal-close" onClick={() => { setDetailModal(null); setConfirmAction(null); }}>✕</button>
              </div>

              <div className="so-modal-body">
                {/* Price hero */}
                <div className="so-price-hero">
                  <div className="so-price-hero-amount">{formatCurrency(detailModal.offerPrice)}</div>
                  {diff && listPrice && (
                    <div className={`so-price-hero-diff${diff.above ? '' : ' below'}`}>
                      {diff.above ? '+' : ''}{formatCurrency(Math.abs(Number(detailModal.offerPrice) - Number(listPrice)))} {diff.above ? 'above' : 'below'} list price ({diff.label})
                    </div>
                  )}
                </div>

                {/* Financial Terms */}
                <div className="so-modal-section">
                  <div className="so-modal-section-title">Financial Terms</div>
                  <div className="so-modal-row"><span className="so-modal-lbl">Offer Price</span><span className="so-modal-val purple">{formatCurrency(detailModal.offerPrice)}</span></div>
                  {detailModal.financingType && <div className="so-modal-row"><span className="so-modal-lbl">Financing Type</span><span className="so-modal-val">{detailModal.financingType}</span></div>}
                  {detailModal.downPaymentPercent != null && <div className="so-modal-row"><span className="so-modal-lbl">Down Payment</span><span className="so-modal-val">{detailModal.downPaymentPercent}%{detailModal.offerPrice ? ` · ${formatCurrency(Number(detailModal.offerPrice) * Number(detailModal.downPaymentPercent) / 100)}` : ''}</span></div>}
                  {detailModal.earnestMoney > 0 && <div className="so-modal-row"><span className="so-modal-lbl">Earnest Money (EMD)</span><span className="so-modal-val">{formatCurrency(detailModal.earnestMoney)}{detailModal.offerPrice ? ` (${((Number(detailModal.earnestMoney) / Number(detailModal.offerPrice)) * 100).toFixed(1)}%)` : ''}</span></div>}
                </div>

                {/* Timeline & Contingencies */}
                <div className="so-modal-section">
                  <div className="so-modal-section-title">Timeline &amp; Contingencies</div>
                  {detailModal.closingDays > 0 && <div className="so-modal-row"><span className="so-modal-lbl">Close of Escrow</span><span className="so-modal-val">{detailModal.closingDays} days from acceptance</span></div>}
                  {fmtContingencies(detailModal.contingencies) && <div className="so-modal-row"><span className="so-modal-lbl">Contingencies</span><span className="so-modal-val">{fmtContingencies(detailModal.contingencies)}</span></div>}
                  {detailModal.notes && <div className="so-modal-row"><span className="so-modal-lbl">Notes</span><span className="so-modal-val" style={{ maxWidth: '60%', textAlign: 'right' }}>{detailModal.notes}</span></div>}
                </div>

                {/* Package Documents */}
                <div className="so-modal-section">
                  <div className="so-modal-section-title">Package Documents</div>
                  {renderPackageDocs(detailModal).map((d, i) => (
                    <div key={i} className={`so-doc-row${d.ready ? '' : ' missing'}`}>
                      <span className="so-doc-icon">{docIcon(d.name)}</span>
                      <span className="so-doc-name">{d.name}</span>
                      {d.ready ? (
                        <>
                          {d.file?.size && <span className="so-doc-size">{fileSize(d.file.size)}</span>}
                          <button className="so-doc-view-btn" onClick={e => { e.stopPropagation(); handleDownloadDoc(detailModal, d.file); }}>View</button>
                        </>
                      ) : (
                        <span className="so-doc-missing-label">Not uploaded</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer — listing agent actions only */}
              <div className="so-modal-footer">
                <button className="so-footer-btn so-footer-cancel" onClick={() => { setDetailModal(null); setConfirmAction(null); }}>
                  Close
                </button>
                {isListingAgent && (
                  <>
                    {detailModal.sellerInterested && detailModal.envelopeStatus !== 'sent' && detailModal.envelopeStatus !== 'completed' && (
                      <button
                        className="so-footer-btn so-footer-sig"
                        onClick={() => { setSendForSigOffer(detailModal); setSendForSigError(null); setSelectedOfferDocId(null); setShowSendForSigModal(true); }}
                      >
                        ✍ Send for Signature
                      </button>
                    )}
                    {(detailModal.envelopeStatus === 'sent' || detailModal.envelopeStatus === 'completed') && (
                      <span className="so-badge so-badge-accepted" style={{ alignSelf: 'center' }}>✓ Sent to DocuSign</span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Upload Offer Package Modal ──────────────────────────────────────── */}
      {showUploadModal && (
        <div className="so-modal-overlay" onClick={() => { setShowUploadModal(false); resetUpload(); }}>
          <div className="so-modal" style={{ width: 560 }} onClick={e => e.stopPropagation()}>
            <div className="so-modal-header">
              <div>
                <p className="so-modal-title">Upload Offer Package</p>
                <p className="so-modal-sub">Upload all documents received from the buyer's agent</p>
              </div>
              <button className="so-modal-close" onClick={() => { setShowUploadModal(false); resetUpload(); }}>✕</button>
            </div>

            <div className="so-modal-body">
              {/* Offer details — mirrors mobile upload form */}
              <div className="so-modal-section">
                <div className="so-modal-section-title">Offer Details</div>
                <div className="so-form-group">
                  <label className="so-form-label">Offer Price <span style={{ color: '#EF4444' }}>*</span></label>
                  <input
                    className="so-form-input"
                    placeholder="e.g. 890000"
                    inputMode="decimal"
                    value={uploadForm.offerPrice}
                    onChange={e => setUploadForm(p => ({ ...p, offerPrice: e.target.value.replace(/[^0-9.]/g, '') }))}
                    disabled={isUploading}
                  />
                </div>
                <div className="so-form-row">
                  <div className="so-form-group">
                    <label className="so-form-label">Earnest Money (EMD)</label>
                    <input
                      className="so-form-input"
                      placeholder="e.g. 15000"
                      inputMode="decimal"
                      value={uploadForm.earnestMoney}
                      onChange={e => setUploadForm(p => ({ ...p, earnestMoney: e.target.value.replace(/[^0-9.]/g, '') }))}
                      disabled={isUploading}
                    />
                  </div>
                  <div className="so-form-group">
                    <label className="so-form-label">Down Payment %</label>
                    <input
                      className="so-form-input"
                      placeholder="e.g. 20"
                      inputMode="decimal"
                      value={uploadForm.downPaymentPercent}
                      onChange={e => setUploadForm(p => ({ ...p, downPaymentPercent: e.target.value.replace(/[^0-9.]/g, '') }))}
                      disabled={isUploading}
                    />
                  </div>
                </div>
                <div className="so-form-group">
                  <label className="so-form-label">Financing Type</label>
                  <div className="so-financing-chips">
                    {['CONVENTIONAL', 'FHA', 'VA', 'CASH', 'OTHER'].map(ft => (
                      <button
                        key={ft}
                        type="button"
                        className={`so-financing-chip${uploadForm.financingType === ft ? ' active' : ''}`}
                        onClick={() => setUploadForm(p => ({ ...p, financingType: ft }))}
                        disabled={isUploading}
                      >
                        {ft.charAt(0) + ft.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Document Type — slots this upload into the version chain.
                    PA = initial offer, Counter = subsequent round. Backend
                    stamps PA_V1 / Counter_V{n}. */}
                <div className="so-form-group">
                  <label className="so-form-label">Document Type <span style={{ color: '#EF4444' }}>*</span></label>
                  <div className="so-financing-chips">
                    {[
                      { value: 'PURCHASE_AGREEMENT', label: 'Purchase Agreement' },
                      { value: 'COUNTER_OFFER',      label: 'Counter Offer' },
                    ].map(dt => (
                      <button
                        key={dt.value}
                        type="button"
                        className={`so-financing-chip${uploadForm.documentType === dt.value ? ' active' : ''}`}
                        onClick={() => setUploadForm(p => ({ ...p, documentType: dt.value }))}
                        disabled={isUploading}
                      >
                        {dt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="so-form-group">
                  <label className="so-form-label">Close of Escrow (days)</label>
                  <input
                    className="so-form-input"
                    placeholder="e.g. 30"
                    inputMode="numeric"
                    value={uploadForm.closingDays}
                    onChange={e => setUploadForm(p => ({ ...p, closingDays: e.target.value.replace(/[^0-9]/g, '') }))}
                    disabled={isUploading}
                  />
                </div>
              </div>

              {/* Drop zone */}
              <div className="so-form-group" style={{ marginBottom: 6 }}>
                <label className="so-form-label">Documents <span style={{ color: '#EF4444' }}>*</span></label>
              </div>
              <div
                className={`so-drop-zone${isDragging ? ' drag-over' : ''}`}
                onDragOver={e => { e.preventDefault(); if (!isUploading) setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={isUploading ? e => e.preventDefault() : handleDrop}
                onClick={() => { if (!isUploading) fileInputRef.current?.click(); }}
                style={isUploading ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <div className="so-drop-icon">📁</div>
                <div className="so-drop-title">Click to upload or drag files here</div>
                <div className="so-drop-sub">PDF only · Max 10 MB per file · Multiple files allowed</div>
              </div>
              <input type="file" ref={fileInputRef} accept="application/pdf" multiple style={{ display: 'none' }} onChange={handleFileSelect} />

              {/* Uploaded files list */}
              {uploadFiles.length > 0 && (
                <div className="so-file-list">
                  {uploadFiles.map((f, i) => (
                    <div key={i} className="so-file-item">
                      <span style={{ fontSize: 18 }}>📄</span>
                      <span className="so-file-name">{f.name}</span>
                      <span className="so-file-size">{fileSize(f.size)}</span>
                      <button className="so-file-remove" onClick={() => setUploadFiles(prev => prev.filter((_, idx) => idx !== i))} disabled={isUploading}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Notes — placed after documents (mirrors mobile field order) */}
              <div className="so-form-group" style={{ marginTop: 4 }}>
                <label className="so-form-label">Notes</label>
                <textarea
                  className="so-form-input so-form-textarea"
                  placeholder="Any additional notes about this offer…"
                  rows={3}
                  value={uploadForm.notes}
                  onChange={e => setUploadForm(p => ({ ...p, notes: e.target.value }))}
                  disabled={isUploading}
                />
              </div>

              <div className="so-notice">
                <svg className="so-notice-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span>Once uploaded, the seller will be notified and can view the package summary. Full documents are accessible via "View Package".</span>
              </div>

              {uploadError && (
                <p style={{ color: '#DC2626', fontSize: 13, fontWeight: 600, marginTop: 12 }}>{uploadError}</p>
              )}
            </div>

            <div className="so-modal-footer">
              <button className="so-footer-btn so-footer-cancel" onClick={() => { setShowUploadModal(false); resetUpload(); }} disabled={isUploading}>
                Cancel
              </button>
              <button
                className="so-footer-btn so-footer-accept"
                onClick={handleUploadSubmit}
                disabled={isUploading || !uploadForm.offerPrice.trim() || uploadFiles.length === 0}
              >
                {isUploading ? 'Uploading…' : 'Upload Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Send for Signature Confirmation Modal ──────────────────────────── */}
      {showSendForSigModal && sendForSigOffer && (() => {
        const allParticipants = participants || transaction?.participants || [];
        const getInitials  = (name = '') => name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
        const ROLE_LABEL   = { LISTING_AGENT: 'Listing Agent', SELLER: 'Seller', BUYER_AGENT: "Buyer's Agent", BUYER: 'Buyer', AGENT: 'Agent', TRANSACTION_COORDINATOR: 'Transaction Coordinator' };
        const listingAgent = allParticipants.find(p => (p.role || '').toUpperCase() === 'LISTING_AGENT');
        const otherSigners = allParticipants.filter(p => (p.role || '').toUpperCase() !== 'LISTING_AGENT');
        const signers      = [listingAgent && { ...listingAgent, label: 'Listing Agent' }, ...otherSigners.map(p => ({ ...p, label: ROLE_LABEL[(p.role || '').toUpperCase()] || p.role || 'Participant' }))].filter(Boolean);
        const offerDocs    = (sendForSigOffer.documents || []).filter(d => d.status === 'RECEIVED' || d.status == null);
        const closeModal   = () => { if (!isSendingForSig) { setShowSendForSigModal(false); setSendForSigOffer(null); setSendForSigError(null); setSelectedOfferDocId(null); } };
        return (
          <div className="so-modal-overlay" onClick={closeModal}>
            <div className="so-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
              <div className="so-modal-header">
                <div>
                  <p className="so-modal-title">Send for Signature</p>
                  <p className="so-modal-sub">Send offer from <strong>{sendForSigOffer.buyerName}</strong> for DocuSign signature?</p>
                </div>
                <button className="so-modal-close" onClick={closeModal}>✕</button>
              </div>
              <div className="so-modal-body">
                {offerDocs.length > 0 && (
                  <div className="so-sig-order-box" style={{ marginBottom: 12 }}>
                    <p className="so-sig-order-label">Select Document</p>
                    {offerDocs.map((doc) => {
                      const docId   = doc.documentId;
                      const docName = doc.name || doc.fileName || 'Document';
                      return (
                        <div key={docId} className="so-doc-select-row" onClick={() => setSelectedOfferDocId(docId)}>
                          <input
                            type="checkbox"
                            className="so-doc-select-check"
                            checked={selectedOfferDocId === docId}
                            onChange={() => setSelectedOfferDocId(docId)}
                          />
                          <span className="so-doc-select-name">{docName}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {signers.length > 0 && (
                  <div className="so-sig-order-box">
                    <p className="so-sig-order-label">Signing Order</p>
                    {signers.map((signer, idx) => (
                      <div key={signer.id || idx} className="so-sig-order-row">
                        <div className="so-sig-order-num">{idx + 1}</div>
                        <div className="so-sig-order-avatar">{getInitials(signer.name)}</div>
                        <div className="so-sig-order-info">
                          <span className="so-sig-order-name">{signer.name}</span>
                          <span className="so-sig-order-role">{signer.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {sendForSigError && (
                  <p style={{ color: '#DC2626', fontSize: 13, fontWeight: 600, marginTop: 12 }}>{sendForSigError}</p>
                )}
              </div>
              <div className="so-modal-footer">
                <button className="so-footer-btn so-footer-cancel" onClick={closeModal} disabled={isSendingForSig}>
                  Cancel
                </button>
                <button className="so-footer-btn so-footer-sig" onClick={handleSendForSignature} disabled={isSendingForSig || !selectedOfferDocId}>
                  {isSendingForSig ? 'Sending…' : '> Send'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Offer summary modal — one at a time, driven by the hook's target. */}
      {offerAi.target?.summaryId && (
        <OfferAiSummaryModal
          transactionId={transaction?.id}
          summaryId={offerAi.target.summaryId}
          onClose={offerAi.close}
        />
      )}

      {/* Offer comparison modal — single-instance, opened from the header strip */}
      {offerComparison.isOpen && offerComparison.comparison?.status === 'READY' && (
        <OfferComparisonModal
          transactionId={transaction?.id}
          initial={offerComparison.comparison}
          offerLabelByOfferId={new Map(
            offers.map((o) => [o.id, o.buyerName || `Offer ${o.id.slice(0, 8)}`])
          )}
          onClose={offerComparison.close}
          onRegenerate={offerComparison.regenerate}
        />
      )}
    </div>
  );
};

export default SellerOffers;
